export const SUBSCRIPTION_STATUS = {
  UNSUBSCRIBED: 'unsubscribed',
  SUBSCRIBED: 'subscribed',
  LAPSED: 'lapsed',
} as const;

// Stripe-style statuses that count as an entitled subscription. Shared so
// every gate keyed off a subscriptionStatus/teamSubscriptionStatus string
// (RevenueCat purchase confirmation, iOS project-limit gating, etc.) treats
// trialing the same as active instead of re-deriving its own status list.
export const ACTIVE_BILLING_STATUSES: readonly string[] = Object.freeze([
  'active',
  'trialing',
]);

export const SESSION_SELECTED_TEAM_NAME: string =
  'selectedTeamName_Subscription' as const;

export const WEB_PLAN_NAME: string = 'team' as const;
export const MOBILE_PLAN_NAME: string = 'personal' as const;
export const URL_HowDoIUpgradeaTeamPlan =
  'https://portal.tasktag.com/blog/how-do-i-upgrade-to-a-team-plan';
export const CDN_BASE_URL = 'https://ik.imagekit.io/nzfp9w26m';
export const APP_WEBSITE_URL = 'https://app.tasktag.com';
// TA-10914 (PR review): bumped from 'AvatarId_'/'AvatarId_thumb_' to 'v2'.
// Pre-fix code could persist an already-stale avatar URL under Apollo
// cache-first with a fresh `Date.now()` timestamp; `shouldRefetchAvatarUrl`
// trusts any entry with a recent timestamp without re-parsing the URL's own
// expiry, so those already-poisoned entries would otherwise keep looking
// "fresh" for up to 7 more days after this fix ships. Changing the key
// prefix makes every pre-fix entry a cache miss on next read — a clean,
// one-time invalidation instead of auditing/repairing existing entries.
export const AvatarCachePrefix = 'AvatarId_v2_';
export const AvatarThumbnailCachePrefix = 'AvatarId_thumb_v2_';

export const FCMToken = 'FCMToken';

export const ALLOWED_FREE_PERSONAL_PROJECTS = 3;

export const PROJECT_NAME_LENGTH_LIMIT = 255;
export const PROJECT_DESC_LENGTH_LIMIT = 1000;
export const TASK_NAME_LENGTH_LIMIT = 255;
export const TASK_DESC_LENGTH_LIMIT = 1000;

// Staleness window shared by the Tasks list screens (native Tasks.tsx and
// Tasks.web.tsx) for useRefetchOnFocus. A focus event within this window of
// the last successful refetch is skipped, so navigating to a task detail and
// straight back does not fire a redundant listTasks request (TA-10055/TA-10046).
export const TASK_LIST_REFETCH_MIN_AGE_MS = 30_000;

// TA-10306: Project Detail's data is already kept live by the app-wide
// projectUpdated subscription (useProjectMemberAddedNotification patches the
// Project entity into the cache unconditionally on every event), so a
// network refetch on every focus was pure waste. But that subscription only
// reaches explicit project members and isn't triggered by every backend
// change (e.g. checklists, invitations, team/subscription details, member
// removal). This window is a safety net so a focus-triggered refetch still
// self-heals any such gap within a few minutes, rather than either
// refetching on every focus (the old stalenessMs=0 behavior) or never
// refetching again.
export const PROJECT_DETAIL_REFETCH_MIN_AGE_MS = 300_000;
export const CHECKLIST_ITEM_LENGTH_LIMIT = 255;
// TA-11020: mobile task and project checklist rows start a drag from a
// long-press. Without an explicit delay RN's 500ms default applied, long
// enough that users released first and read the drag handle as broken. No
// threshold was ever specified in design; 150ms is the working spec. Task
// cards (TaskCard, 200ms) are a whole-card long-press that must also tell a
// drag apart from a tap or scroll, so they keep their own value. Web lists
// use dnd-kit's 6px distance constraint and have no delay.
export const CHECKLIST_DRAG_LONG_PRESS_DELAY_MS = 150;
// TA-11020: left padding (theme spacing key) of a mobile checklist row. The
// drag handle starts here, so the iOS swipe-back edge on the checklist
// screens is narrowed to this width (useChecklistBackSwipeEdge).
export const CHECKLIST_ROW_PADDING_LEFT = 'm';

// TA-11139: My Tasks refreshes when a projectUpdated payload arrives
// (useRefreshOnProjectUpdated), which the backend publishes to every project
// member when a task is created, completed, updated, deleted or assigned. It
// replaced a 30 s poll (TA-10460, which only evaluated meUpdated as a push
// signal). One task change can publish several events, so they are collapsed
// by a trailing debounce of this length.
export const MY_TASKS_PROJECT_UPDATED_DEBOUNCE_MS = 1_000;
// The event covers every project the viewer belongs to, so steady activity
// elsewhere must not postpone the refresh for long: cap the debounce.
export const MY_TASKS_PROJECT_UPDATED_MAX_WAIT_MS = 5_000;
// Safety net for changes that publish no projectUpdated (e.g. a personal task
// with no project, update_task_members). Like the push refresh, it only runs
// while My Tasks is focused and the app is in the foreground.
export const MY_TASKS_SAFETY_POLL_INTERVAL_MS = 300_000;

// Shared gating/timing for API-backed search inputs (see useDebouncedSearch).
// TA-10091: standardizes the ~40 call sites that previously each declared
// their own local DEBOUNCE constant (values ranged 0/500/800/1000ms).
export const MIN_SEARCH_CHARS = 3;
export const SEARCH_DEBOUNCE_MS = 300;

export const TEAM_PLAN_FEATURES = [
  '2TB shared team storage',
  'Unlimited projects & tasks',
  'Add unlimited users to projects & tasks',
  'Team admin & member roles',
  'Centralized billing',
  'Global activity log for full visibility',
];

export const MIXPANEL_EVENTS = {
  // Pre-auth carousel events
  APP_INTRO_STARTED: 'app_intro_started',
  APP_INTRO_STEP_VIEWED: 'app_intro_step_viewed',
  APP_INTRO_SKIPPED: 'app_intro_skipped',
  APP_INTRO_COMPLETED: 'app_intro_completed',
  // Onboarding tutorialization events
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_CARD_WATCHED: 'onboarding_card_watched',
  ONBOARDING_BANNER_DISMISSED: 'onboarding_banner_dismissed',
  // Owner nudge events
  OWNER_NUDGE_STEP_COMPLETED: 'owner_nudge_step_completed',
  OWNER_NUDGE_COMPLETED: 'owner_nudge_completed',
  OWNER_NUDGE_DISMISSED: 'owner_nudge_dismissed',
  INVITE_MEMBER_NUDGE_DISMISSED: 'invite_member_nudge_dismissed',
  INVITE_MEMBER_NUDGE_COMPLETED: 'invite_member_nudge_completed',
  CHAT_NUDGE_DISMISSED: 'chat_nudge_dismissed',
  CHAT_NUDGE_COMPLETED: 'chat_nudge_completed',
  TASK_NUDGE_DISMISSED: 'task_nudge_dismissed',
  TASK_NUDGE_COMPLETED: 'task_nudge_completed',
  ASSIGN_TASK_NUDGE_DISMISSED: 'assign_task_nudge_dismissed',
  ASSIGN_TASK_NUDGE_COMPLETED: 'assign_task_nudge_completed',
  // Member task nudge events
  MEMBER_TASK_NUDGE_STEP_COMPLETED: 'member_task_nudge_step_completed',
  MEMBER_TASK_NUDGE_COMPLETED: 'member_task_nudge_completed',
  MEMBER_TASK_NUDGE_DISMISSED: 'member_task_nudge_dismissed',
  ENTERED_DASHBOARD: 'entered_dashboard',
  WORKSPACE_CREATED: 'workspace_created',
  PROJECT_CREATED: 'project_created',
  NAVIGATED_TO_PROJECT: 'navigated_to_project',
  VIEWED_PROJECT_OVERVIEW: 'viewed_project_overview',
  TASK_CREATED: 'task_created',
  TASK_COMPLETED: 'task_completed',
  TASK_REOPENED: 'task_reopened',
  TASK_ASSIGNED: 'task_assigned',
  TASK_COMMENT_ADDED: 'task_comment_added',
  CHECKLIST_ITEM_TOGGLED: 'checklist_item_toggled',
  VIEWED_ACTIVITY_LOG: 'viewed_activity_log',
  CLICKED_ACTIVITY_ITEM: 'clicked_activity_item',
  FILTERED_ACTIVITY_LOG: 'filtered_activity_log',
  TEAM_MEMBER_INVITED: 'team_member_invited',
  TEAM_MEMBER_ROLE_CHANGED: 'team_member_role_changed',
  VIEWED_TEAM_SETTINGS: 'viewed_team_settings',
  LEFT_TEAM: 'left_team',
  SWITCHED_TEAM: 'switched_team',
  OPENED_TIME_TRACKER: 'opened_time_tracker',
  OPENED_INVOICE_TAB: 'opened_invoice_tab',
  UPLOADED_DOCUMENT: 'uploaded_document',
  VIEWED_REPORTS_PAGE: 'viewed_reports_page',
  API_ERROR_ENCOUNTERED: 'api_error_encountered',
  FORM_SUBMISSION_FAILED: 'form_submission_failed',
  UI_INTERACTION_TIMEOUT: 'ui_interaction_timeout',
  PAGE_LOAD_TIME_EXCEEDED: 'page_load_time_exceeded',
  TEAM_INVITE_SENT: 'team_invite_sent',
  TEAM_INVITE_ACCEPTED: 'team_invite_accepted',
  USER_SIGNED_UP: 'user_signed_up',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  TEAM_CREATED: 'team_created',
  CHAT_OPENED: 'chat_opened',
  MESSAGE_SENT: 'message_sent',
  PUSH_NOTIFICATION_OPENED: 'push_notification_opened',
  SEARCH_PERFORMED: 'search_performed',
  SUBSCRIPTION_STARTED: 'subscription_started',
  TRIAL_STARTED: 'trial_started',
  PLAN_UPGRADED: 'plan_upgraded',
  // Paywall modal
  PAYWALL_MODAL_SHOWN: 'paywall_modal_shown',
  PAYWALL_CTA_CLICKED: 'paywall_cta_clicked',
  PAYWALL_DISMISSED: 'paywall_dismissed',
  // Rate Us prompt (TA-9185)
  RATE_US_MODAL_SHOWN: 'rate_us_modal_shown',
  RATE_US_LOVE_IT_TAPPED: 'rate_us_love_it_tapped',
  RATE_US_LATER_TAPPED: 'rate_us_later_tapped',
  RATE_US_TRIGGER_HIT: 'rate_us_trigger_hit',
  // "New Update" FAB (TA-10731). Paired deliberately: the field report was
  // "the button does nothing", which is only visible in the gap between the
  // tap and the options sheet actually opening. Compare the two counts by
  // `source` to spot it happening again without needing a repro.
  NEW_UPDATE_TAPPED: 'new_update_tapped',
  NEW_UPDATE_SHEET_OPENED: 'new_update_sheet_opened',
};
